/**
 * View mapping for the stocked-items selling-price recalculation screen.
 *
 * Kept free of Angular so the before/after column building stays a pure
 * transformation of what `POST /products/pawns/calculate-stocked-items-selling-price`
 * returns. Every entry of that response carries BOTH sides of the comparison
 * (`current*` and `new*`); which side reaches the screen is decided here: the two
 * "new" columns must stay blank until the operator presses Recalculate.
 */

const NO_VALUE = '-';

/** Mirrors the reprice screen's rate rendering: `2,500 PHP`. */
function formatRate(pricePerGram) {
  return pricePerGram == null ? NO_VALUE : `${pricePerGram.toLocaleString()} PHP`;
}

/**
 * One label per item, joining every metal component. `karatRates` holds one entry
 * per component, so a plain item yields a single value and a bi/tri-colour item a
 * comma-separated list. An item with no metal has an empty list.
 */
function joinComponents(karatRates, mapComponent) {
  if (!karatRates || !karatRates.length) {
    return NO_VALUE;
  }
  return karatRates.map(mapComponent).join(', ');
}

function buildRow(item, includeNewValues) {
  return {
    id: item.id,
    itemNumber: item.itemNumber,
    autoDescription: item.autoDescription == null ? NO_VALUE : item.autoDescription,
    currentKaratLabel: joinComponents(item.karatRates, rate => rate.fineness),
    currentKaratRateLabel: joinComponents(item.karatRates, rate => formatRate(rate.currentPricePerGram)),
    currentSellingPrice: item.currentSellingPrice,
    newKaratRateLabel: includeNewValues
      ? joinComponents(item.karatRates, rate => formatRate(rate.newPricePerGram))
      : null,
    // Held as the raw number the preview returned: the command compares it rather
    // than writing it, so it has to travel back untouched.
    newSellingPrice: includeNewValues ? item.newSellingPrice : null
  };
}

/** Rows as the screen opens: the stored values only, both new columns blank. */
export function buildCurrentRows(preview) {
  return (preview || []).map(item => buildRow(item, false));
}

/** Rows after Recalculate: the same items with the new columns filled in. */
export function buildRecalculatedRows(preview) {
  return (preview || []).map(item => buildRow(item, true));
}

/** Apply is only offered once every listed item carries a recomputed price. */
export function hasCompleteRecalculation(rows) {
  return !!rows && rows.length > 0 && rows.every(row => row.newSellingPrice != null);
}

/** The `RecalculateStockedItemsSellingPrice` payload. */
export function toCommandInput(rows) {
  return {
    items: rows.map(row => ({
      id: row.id,
      newSellingPrice: row.newSellingPrice
    }))
  };
}
